import React, { useState, useEffect } from 'react';
import { 
  Calendar, Clock, User, Phone, Mail, X, ChevronLeft, ChevronRight, LogOut, Search, 
  CheckCircle, XCircle, Eye, AlertCircle, Menu, Settings, Briefcase, Home, Edit2, 
  Trash2, Plus, Save, Building2, Users, ExternalLink,
  Bell, CheckSquare, Square, Heart, Activity, Syringe, Pill, 
  Thermometer, Microscope, Stethoscope, Upload, RefreshCw, BookUser, PhoneCall
} from 'lucide-react';

const POCKETBASE_URL = 'https://medvista.online';
const POCKETBASE_USERNAME = 'app-access-01@medvista.online';
const POCKETBASE_PASSWORD = '9ygc90ezomgt82l';

let authToken = null;

const authenticatePocketBase = async () => {
  if (authToken) return authToken;
  
  try {
    const response = await fetch(`${POCKETBASE_URL}/api/collections/_superusers/auth-with-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        identity: POCKETBASE_USERNAME,
        password: POCKETBASE_PASSWORD
      })
    });
    
    if (!response.ok) {
      throw new Error('Authentication failed');
    }
    
    const data = await response.json();
    authToken = data.token;
    return authToken;
  } catch (error) {
    console.error('Auth error:', error);
    return null;
  }
};

// Icon components mapping
const IconComponents = {
  'Heart': Heart,
  'Activity': Activity,
  'Eye': Eye,
  'Stethoscope': Stethoscope,
  'Syringe': Syringe,
  'Pill': Pill,
  'Thermometer': Thermometer,
  'Microscope': Microscope,
  'Calendar': Calendar,
  'Clock': Clock,
  'User': User,
  'Phone': Phone,
  'Mail': Mail,
  'Building': Building2,
  'Briefcase': Briefcase
};

// Color mapping for services with proper colors
const colorMap = {
  blue: { bg: '#DBEAFE', text: '#1E40AF', border: '#3B82F6' },
  red: { bg: '#FEE2E2', text: '#991B1B', border: '#EF4444' },
  pink: { bg: '#FCE7F3', text: '#9F1239', border: '#EC4899' },
  purple: { bg: '#F3E8FF', text: '#6B21A8', border: '#A855F7' },
  green: { bg: '#D1FAE5', text: '#065F46', border: '#10B981' },
  orange: { bg: '#FFEDD5', text: '#9A3412', border: '#F97316' },
  yellow: { bg: '#FEF3C7', text: '#92400E', border: '#F59E0B' },
  indigo: { bg: '#E0E7FF', text: '#3730A3', border: '#6366F1' },
  teal: { bg: '#CCFBF1', text: '#115E59', border: '#14B8A6' }
};

// Toast notification component
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === 'success' ? 'bg-green-500' : type === 'error' ? 'bg-red-500' : 'bg-blue-500';

  return (
    <div className={`fixed top-4 right-4 ${bgColor} text-white px-6 py-3 rounded-xl shadow-lg z-50 flex items-center gap-3`}>
      {type === 'success' && <CheckCircle className="w-5 h-5" />}
      {type === 'error' && <AlertCircle className="w-5 h-5" />}
      {type === 'info' && <Bell className="w-5 h-5" />}
      <span>{message}</span>
      <button onClick={onClose} className="hover:bg-white hover:bg-opacity-20 rounded p-1">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

// Contact Card Component with QR
const ContactCard = ({ contact }) => {
  const [qrCode, setQrCode] = useState(null);
  const [showQR, setShowQR] = useState(false);

  const generateQRCode = () => {
    try {
      const cleanPhone = contact.phone.replace(/\D/g, '');
      const telUrl = `tel:+${cleanPhone}`;
      
      // Using a more reliable QR code API
      const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(telUrl)}`;
      setQrCode(qrUrl);
      setShowQR(true);
    } catch (error) {
      console.error('Error generating QR code:', error);
    }
  };

  return (
    <>
      <div className="border-2 border-gray-200 rounded-xl p-4 hover:shadow-lg transition-all">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 className="font-bold text-gray-900">{contact.name} {contact.surname}</h3>
            <p className="text-sm text-gray-600">{contact.date_of_birth}</p>
          </div>
          <div className="text-right text-xs text-gray-500">
            <p>{contact.bookingsCount} termin{contact.bookingsCount > 1 ? 'a' : ''}</p>
          </div>
        </div>
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-gray-400" />
            <a href={`tel:${contact.phone}`} className="text-blue-600 hover:underline font-medium">
              {contact.phone}
            </a>
          </div>
          {contact.email && (
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-gray-400" />
              <a href={`mailto:${contact.email}`} className="text-blue-600 hover:underline">
                {contact.email}
              </a>
            </div>
          )}
          <div className="pt-2 border-t border-gray-200">
            <button
              onClick={generateQRCode}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:from-blue-600 hover:to-cyan-600 font-semibold text-sm"
            >
              <PhoneCall className="w-4 h-4" />
              QR poziv
            </button>
          </div>
        </div>
      </div>

      {showQR && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setShowQR(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">QR Kod za poziv</h3>
              <button onClick={() => setShowQR(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-4">
                {contact.name} {contact.surname}
              </p>
              {qrCode && (
                <img src={qrCode} alt="QR Code" className="mx-auto mb-4 border-4 border-gray-200 rounded-lg" />
              )}
              <p className="text-xs text-gray-500">Skenirajte QR kod da pozovete</p>
              <p className="font-mono text-sm mt-2">{contact.phone}</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const BackofficeApp = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [loginError, setLoginError] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [toast, setToast] = useState(null);
  const [hasNewBookings, setHasNewBookings] = useState(false);

  const [loginForm, setLoginForm] = useState({
    username: '',
    password: ''
  });

  // Dashboard state
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editBookingForm, setEditBookingForm] = useState({});
  const [viewMode, setViewMode] = useState('day');

  // Services state
  const [services, setServices] = useState([]);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceForm, setServiceForm] = useState({
    name: '',
    description: '',
    duration: '',
    price: '',
    picture: 'Stethoscope',
    color: 'blue'
  });

  // Tenant state
  const [tenantData, setTenantData] = useState(null);
  const [tenantForm, setTenantForm] = useState({});
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [tenantLogo, setTenantLogo] = useState(null);

  // Users state
  const [users, setUsers] = useState([]);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    username: '',
    password: '',
    full_name: '',
    email: '',
    role: 'staff',
    is_active: 'true'
  });

  // Contacts state
  const [contacts, setContacts] = useState([]);
  const [contactsSearch, setContactsSearch] = useState('');
  const [filteredContacts, setFilteredContacts] = useState([]);

  // Profile state
  const [profileForm, setProfileForm] = useState({
    full_name: '',
    email: '',
    new_password: '',
    confirm_password: ''
  });

  const availableIcons = Object.keys(IconComponents);
  const availableColors = ['blue', 'red', 'pink', 'purple', 'green', 'orange', 'yellow', 'indigo', 'teal'];

  const isAdmin = currentUser?.role === 'administrator';

  useEffect(() => {
    const storedUser = localStorage.getItem('backoffice_user');
    if (storedUser) {
      setCurrentUser(JSON.parse(storedUser));
      setIsLoggedIn(true);
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn && currentUser) {
      loadTenantLogo();
      if (currentPage === 'dashboard') {
        loadBookings();
      } else if (currentPage === 'services') {
        loadServices();
      } else if (currentPage === 'tenant') {
        loadTenantData();
      } else if (currentPage === 'users') {
        loadUsers();
      } else if (currentPage === 'contacts') {
        loadContacts();
      }
    }
  }, [isLoggedIn, currentUser, currentPage, selectedDate, selectedMonth, viewMode]);

  useEffect(() => {
    filterBookings();
  }, [bookings, searchQuery, statusFilter, selectedDate, selectedMonth, viewMode]);

  useEffect(() => {
    if (currentUser) {
      setProfileForm({
        full_name: currentUser.full_name || '',
        email: currentUser.email || '',
        new_password: '',
        confirm_password: ''
      });
    }
  }, [currentUser]);

  useEffect(() => {
    filterContacts();
  }, [contacts, contactsSearch]);

  useEffect(() => {
    if (isLoggedIn && currentUser && currentPage === 'dashboard') {
      const interval = setInterval(() => {
        loadBookings(true);
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [isLoggedIn, currentUser, currentPage]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const loadTenantLogo = async () => {
    try {
      const token = await authenticatePocketBase();
      if (!token) return;

      const response = await fetch(
        `${POCKETBASE_URL}/api/collections/tenant_details_table/records?filter=(tenant_name='${currentUser.tenant_name}')`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token
          }
        }
      );

      if (!response.ok) return;

      const result = await response.json();
      if (result.items && result.items.length > 0) {
        const data = result.items[0];
        if (data.logo) {
          setTenantLogo(`${POCKETBASE_URL}/api/files/${data.collectionId}/${data.id}/${data.logo}`);
        }
      }
    } catch (error) {
      console.error('Error loading tenant logo:', error);
    }
  };

  const loadBookings = async (silent = false) => {
    try {
      const token = await authenticatePocketBase();
      if (!token) return;

      const response = await fetch(
        `${POCKETBASE_URL}/api/collections/tenant_booked_slots_table/records?filter=(tenant_name='${currentUser.tenant_name}')&sort=-schedule_date,-schedule_start_time&perPage=500`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch bookings');
      }

      const result = await response.json();
      const newBookings = result.items || [];
      
      if (silent && bookings.length > 0) {
        const lastSeenId = localStorage.getItem(`last_booking_${currentUser.tenant_name}`);
        const latestId = newBookings.length > 0 ? newBookings[0].id : null;
        
        if (lastSeenId && latestId && lastSeenId !== latestId) {
          setHasNewBookings(true);
          showToast('Novo zakazivanje!', 'info');
        }
      }
      
      setBookings(newBookings);
    } catch (error) {
      console.error('Error loading bookings:', error);
      if (!silent) showToast('Greška pri učitavanju termina', 'error');
    }
  };

  const markBookingsAsSeen = () => {
    if (bookings.length > 0) {
      localStorage.setItem(`last_booking_${currentUser.tenant_name}`, bookings[0].id);
      setHasNewBookings(false);
    }
  };

  const loadServices = async () => {
    try {
      const token = await authenticatePocketBase();
      if (!token) return;

      const response = await fetch(
        `${POCKETBASE_URL}/api/collections/tenant_services_table/records?filter=(tenant_name='${currentUser.tenant_name}')`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch services');
      }

      const result = await response.json();
      setServices(result.items || []);
    } catch (error) {
      console.error('Error loading services:', error);
      showToast('Greška pri učitavanju usluga', 'error');
    }
  };

  const loadTenantData = async () => {
    try {
      const token = await authenticatePocketBase();
      if (!token) return;

      const response = await fetch(
        `${POCKETBASE_URL}/api/collections/tenant_details_table/records?filter=(tenant_name='${currentUser.tenant_name}')`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch tenant data');
      }

      const result = await response.json();
      if (result.items && result.items.length > 0) {
        const data = result.items[0];
        setTenantData(data);
        setTenantForm({
          name: data.name || '',
          description: data.description || '',
          about: data.about || '',
          address: data.address || '',
          place: data.place || '',
          country: data.country || '',
          telephone: data.telephone || '',
          telephone2: data.telephone2 || '',
          email: data.email || '',
          web: data.web || '',
          monday: data.monday || '',
          tuesday: data.tuesday || '',
          wednesday: data.wednesday || '',
          thursday: data.thursday || '',
          friday: data.friday || '',
          saturday: data.saturday || '',
          sunday: data.sunday || ''
        });
        
        if (data.logo) {
          setLogoPreview(`${POCKETBASE_URL}/api/files/${data.collectionId}/${data.id}/${data.logo}`);
        }
      }
    } catch (error) {
      console.error('Error loading tenant data:', error);
      showToast('Greška pri učitavanju podataka', 'error');
    }
  };

  const loadUsers = async () => {
    try {
      const token = await authenticatePocketBase();
      if (!token) return;

      const response = await fetch(
        `${POCKETBASE_URL}/api/collections/backoffice_users_table/records?filter=(tenant_name='${currentUser.tenant_name}')`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const result = await response.json();
      setUsers(result.items || []);
    } catch (error) {
      console.error('Error loading users:', error);
      showToast('Greška pri učitavanju korisnika', 'error');
    }
  };

  const loadContacts = async () => {
    try {
      const token = await authenticatePocketBase();
      if (!token) return;

      const filter = encodeURIComponent(`tenant_name='${currentUser.tenant_name}'`);

      const response = await fetch(
        `${POCKETBASE_URL}/api/collections/tenant_booked_slots_table/records?filter=${filter}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token.startsWith('Bearer ') ? token : `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch contacts: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      const contactsMap = new Map();
      (result.items || []).forEach(booking => {
        if (!booking.name || !booking.phone) return;

        const key = `${booking.name}_${booking.surname}_${booking.phone}`;
        if (!contactsMap.has(key)) {
          contactsMap.set(key, {
            name: booking.name,
            surname: booking.surname,
            phone: booking.phone,
            email: booking.email || '',
            date_of_birth: booking.date_of_birth || '',
            lastBooking: booking.schedule_date,
            bookingsCount: 1
          });
        } else {
          const existing = contactsMap.get(key);
          existing.bookingsCount += 1;
          if (booking.schedule_date > existing.lastBooking) {
            existing.lastBooking = booking.schedule_date;
          }
        }
      });

      const contactsArray = Array.from(contactsMap.values());
      setContacts(contactsArray);
      setFilteredContacts(contactsArray);
    } catch (error) {
      console.error('Error loading contacts:', error);
      showToast('Greška pri učitavanju imenika', 'error');
    }
  };

  const filterContacts = () => {
    if (!contactsSearch) {
      setFilteredContacts(contacts);
      return;
    }

    const query = contactsSearch.toLowerCase();
    const filtered = contacts.filter(contact =>
      contact.name?.toLowerCase().includes(query) ||
      contact.surname?.toLowerCase().includes(query) ||
      contact.phone?.toLowerCase().includes(query) ||
      contact.email?.toLowerCase().includes(query)
    );
    setFilteredContacts(filtered);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoginError('');

    try {
      const token = await authenticatePocketBase();
      if (!token) {
        throw new Error('Failed to authenticate with server');
      }

      const response = await fetch(
        `${POCKETBASE_URL}/api/collections/backoffice_users_table/records?filter=(username='${loginForm.username}')`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch user data');
      }

      const result = await response.json();

      if (result.items && result.items.length > 0) {
        const user = result.items[0];

        if (user.password !== loginForm.password) {
          setLoginError('Pogrešna lozinka');
          setLoading(false);
          return;
        }

        if (user.is_active !== 'true' && user.is_active !== true) {
          setLoginError('Vaš nalog nije aktivan');
          setLoading(false);
          return;
        }

        const userData = {
          id: user.id,
          username: user.username,
          tenant_name: user.tenant_name,
          role: user.role,
          full_name: user.full_name,
          email: user.email
        };

        setCurrentUser(userData);
        setIsLoggedIn(true);
        localStorage.setItem('backoffice_user', JSON.stringify(userData));
        showToast('Uspešno prijavljivanje', 'success');
      } else {
        setLoginError('Korisnik ne postoji');
      }

      setLoading(false);
    } catch (error) {
      console.error('Login error:', error);
      setLoginError('Greška pri prijavljivanju');
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setCurrentUser(null);
    localStorage.removeItem('backoffice_user');
    setLoginForm({ username: '', password: '' });
    setBookings([]);
    setServices([]);
    setTenantLogo(null);
  };

  const filterBookings = () => {
    let filtered = [...bookings];

    if (viewMode === 'day' && selectedDate) {
      filtered = filtered.filter(b => b.schedule_date === selectedDate);
    } else if (viewMode === 'month' && selectedMonth) {
      filtered = filtered.filter(b => b.schedule_date?.startsWith(selectedMonth));
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(b => 
        b.name?.toLowerCase().includes(query) ||
        b.surname?.toLowerCase().includes(query) ||
        b.phone?.toLowerCase().includes(query) ||
        b.book_id?.toLowerCase().includes(query)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(b => {
        if (statusFilter === 'verified') return b.verified === 'true' || b.verified === true;
        if (statusFilter === 'unverified') return b.verified !== 'true' && b.verified !== true;
        if (statusFilter === 'cancelled') return b.canceled === 'true' || b.canceled === true;
        if (statusFilter === 'pending') {
          return (b.synced !== 'true' && b.synced !== true) && (b.canceled !== 'true' && b.canceled !== true);
        }
        if (statusFilter === 'completed') return b.synced === 'true' || b.synced === true;
        return true;
      });
    }

    // Sortiranje - najnoviji gore (po datumu opadajuće, pa po vremenu opadajuće)
    filtered.sort((a, b) => {
      if (a.schedule_date !== b.schedule_date) {
        return b.schedule_date.localeCompare(a.schedule_date);
      }
      return b.schedule_start_time.localeCompare(a.schedule_start_time);
    });

    setFilteredBookings(filtered);
  };

  const toggleVerified = async (booking) => {
    try {
      const token = await authenticatePocketBase();
      if (!token) return;

      const newVerified = booking.verified === 'true' || booking.verified === true ? 'false' : 'true';

      const response = await fetch(
        `${POCKETBASE_URL}/api/collections/tenant_booked_slots_table/records/${booking.id}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            verified: newVerified
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update booking');
      }

      showToast(newVerified === 'true' ? 'Termin potvrđen' : 'Potvrda uklonjena', 'success');
      loadBookings();
    } catch (error) {
      console.error('Error updating booking:', error);
      showToast('Greška pri ažuriranju', 'error');
    }
  };

  const cancelBooking = async (booking) => {
    try {
      const token = await authenticatePocketBase();
      if (!token) return;

      const response = await fetch(
        `${POCKETBASE_URL}/api/collections/tenant_booked_slots_table/records/${booking.id}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            canceled: 'true'
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to cancel booking');
      }

      showToast('Termin otkazan', 'success');
      loadBookings();
      setShowDetailsModal(false);
    } catch (error) {
      console.error('Error canceling booking:', error);
      showToast('Greška pri otkazivanju', 'error');
    }
  };

  const markAsCompleted = async (booking) => {
    try {
      const token = await authenticatePocketBase();
      if (!token) return;

      const response = await fetch(
        `${POCKETBASE_URL}/api/collections/tenant_booked_slots_table/records/${booking.id}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            synced: 'true'
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update booking');
      }

      showToast('Termin završen', 'success');
      loadBookings();
      setShowDetailsModal(false);
    } catch (error) {
      console.error('Error updating booking:', error);
      showToast('Greška pri ažuriranju', 'error');
    }
  };

  const handleEditBooking = async () => {
    try {
      const token = await authenticatePocketBase();
      if (!token) return;

      const response = await fetch(
        `${POCKETBASE_URL}/api/collections/tenant_booked_slots_table/records/${selectedBooking.id}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            schedule_date: editBookingForm.schedule_date,
            schedule_start_time: editBookingForm.schedule_start_time,
            schedule_end_time: editBookingForm.schedule_end_time
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update booking');
      }

      showToast('Termin ažuriran', 'success');
      loadBookings();
      setShowEditModal(false);
      setShowDetailsModal(false);
    } catch (error) {
      console.error('Error updating booking:', error);
      showToast('Greška pri ažuriranju', 'error');
    }
  };

  const handleServiceSubmit = async () => {
    if (!isAdmin) {
      showToast('Samo administrator može menjati usluge', 'error');
      return;
    }

    try {
      const token = await authenticatePocketBase();
      if (!token) return;

      const serviceData = {
        tenant_name: currentUser.tenant_name,
        name: serviceForm.name,
        description: serviceForm.description,
        duration: serviceForm.duration,
        price: serviceForm.price,
        picture: serviceForm.picture,
        color: serviceForm.color
      };

      const url = editingService 
        ? `${POCKETBASE_URL}/api/collections/tenant_services_table/records/${editingService.id}`
        : `${POCKETBASE_URL}/api/collections/tenant_services_table/records`;

      const response = await fetch(url, {
        method: editingService ? 'PATCH' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(serviceData)
      });

      if (!response.ok) {
        throw new Error('Failed to save service');
      }

      showToast(editingService ? 'Usluga ažurirana' : 'Usluga dodata', 'success');
      loadServices();
      setShowServiceModal(false);
      setEditingService(null);
      setServiceForm({
        name: '',
        description: '',
        duration: '',
        price: '',
        picture: 'Stethoscope',
        color: 'blue'
      });
    } catch (error) {
      console.error('Error saving service:', error);
      showToast('Greška pri čuvanju', 'error');
    }
  };

  const deleteService = async (service) => {
    if (!isAdmin) {
      showToast('Samo administrator može brisati usluge', 'error');
      return;
    }

    try {
      const token = await authenticatePocketBase();
      if (!token) return;

      const response = await fetch(
        `${POCKETBASE_URL}/api/collections/tenant_services_table/records/${service.id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete service');
      }

      showToast('Usluga obrisana', 'success');
      loadServices();
    } catch (error) {
      console.error('Error deleting service:', error);
      showToast('Greška pri brisanju', 'error');
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = async () => {
    if (!isAdmin) {
      showToast('Samo administrator može ukloniti logo', 'error');
      return;
    }

    try {
      const token = await authenticatePocketBase();
      if (!token) return;

      const response = await fetch(
        `${POCKETBASE_URL}/api/collections/tenant_details_table/records/${tenantData.id}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            logo: null
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to remove logo');
      }

      showToast('Logo uklonjen', 'success');
      setLogoPreview(null);
      setLogoFile(null);
      setTenantLogo(null);
      loadTenantData();
    } catch (error) {
      console.error('Error removing logo:', error);
      showToast('Greška pri uklanjanju loga', 'error');
    }
  };

  const handleTenantUpdate = async () => {
    if (!isAdmin) {
      showToast('Samo administrator može menjati podatke', 'error');
      return;
    }

    try {
      const token = await authenticatePocketBase();
      if (!token) return;

      const formData = new FormData();
      
      Object.keys(tenantForm).forEach(key => {
        formData.append(key, tenantForm[key]);
      });

      if (logoFile) {
        formData.append('logo', logoFile);
      }

      const response = await fetch(
        `${POCKETBASE_URL}/api/collections/tenant_details_table/records/${tenantData.id}`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update tenant');
      }

      showToast('Podaci uspešno ažurirani', 'success');
      setLogoFile(null);
      loadTenantData();
      loadTenantLogo();
    } catch (error) {
      console.error('Error updating tenant:', error);
      showToast('Greška pri ažuriranju', 'error');
    }
  };

  const handleUserSubmit = async () => {
    if (!isAdmin) {
      showToast('Samo administrator može upravljati korisnicima', 'error');
      return;
    }

    try {
      const token = await authenticatePocketBase();
      if (!token) return;

      const userData = {
        ...userForm,
        tenant_name: currentUser.tenant_name
      };

      if (editingUser && !userForm.password) {
        delete userData.password;
      }

      const url = editingUser 
        ? `${POCKETBASE_URL}/api/collections/backoffice_users_table/records/${editingUser.id}`
        : `${POCKETBASE_URL}/api/collections/backoffice_users_table/records`;

      const response = await fetch(url, {
        method: editingUser ? 'PATCH' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(userData)
      });

      if (!response.ok) {
        throw new Error('Failed to save user');
      }

      showToast(editingUser ? 'Korisnik ažuriran' : 'Korisnik dodat', 'success');
      loadUsers();
      setShowUserModal(false);
      setEditingUser(null);
      setUserForm({
        username: '',
        password: '',
        full_name: '',
        email: '',
        role: 'staff',
        is_active: 'true'
      });
    } catch (error) {
      console.error('Error saving user:', error);
      showToast('Greška pri čuvanju', 'error');
    }
  };

  const deleteUser = async (user) => {
    if (!isAdmin) {
      showToast('Samo administrator može brisati korisnike', 'error');
      return;
    }

    if (user.id === currentUser.id) {
      showToast('Ne možete obrisati svoj nalog', 'error');
      return;
    }

    try {
      const token = await authenticatePocketBase();
      if (!token) return;

      const response = await fetch(
        `${POCKETBASE_URL}/api/collections/backoffice_users_table/records/${user.id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete user');
      }

      showToast('Korisnik obrisan', 'success');
      loadUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      showToast('Greška pri brisanju', 'error');
    }
  };

  const handleProfileUpdate = async () => {
    try {
      const token = await authenticatePocketBase();
      if (!token) return;

      const updateData = {
        full_name: profileForm.full_name,
        email: profileForm.email
      };

      if (profileForm.new_password) {
        if (profileForm.new_password !== profileForm.confirm_password) {
          showToast('Lozinke se ne poklapaju', 'error');
          return;
        }
        updateData.password = profileForm.new_password;
      }

      const response = await fetch(
        `${POCKETBASE_URL}/api/collections/backoffice_users_table/records/${currentUser.id}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(updateData)
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update profile');
      }

      const updatedUser = {
        ...currentUser,
        full_name: profileForm.full_name,
        email: profileForm.email
      };

      setCurrentUser(updatedUser);
      localStorage.setItem('backoffice_user', JSON.stringify(updatedUser));
      
      setProfileForm({
        ...profileForm,
        new_password: '',
        confirm_password: ''
      });

      showToast('Profil uspešno ažuriran', 'success');
    } catch (error) {
      console.error('Error updating profile:', error);
      showToast('Greška pri ažuriranju', 'error');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const days = ['Ned', 'Pon', 'Uto', 'Sre', 'Čet', 'Pet', 'Sub'];
    const months = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'avg', 'sep', 'okt', 'nov', 'dec'];
    return `${days[date.getDay()]}, ${date.getDate()}. ${months[date.getMonth()]}`;
  };

  const getBookingStatus = (booking) => {
    const now = new Date();
    const bookingDate = new Date(booking.schedule_date);
    const [hours, minutes] = booking.schedule_start_time.split(':');
    bookingDate.setHours(parseInt(hours), parseInt(minutes));

    if (booking.canceled === 'true' || booking.canceled === true) {
      return { label: 'Otkazano', color: 'red', icon: XCircle };
    } else if (booking.synced === 'true' || booking.synced === true) {
      return { label: 'Završeno', color: 'green', icon: CheckCircle };
    } else if (bookingDate < now) {
      return { label: 'Propušteno', color: 'red', icon: AlertCircle };
    } else {
      return { label: 'Zakazano', color: 'blue', icon: Clock };
    }
  };

  const goToPreviousDay = () => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() - 1);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const goToNextDay = () => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + 1);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const goToToday = () => {
    setSelectedDate(new Date().toISOString().split('T')[0]);
  };

  const goToPreviousMonth = () => {
    const [year, month] = selectedMonth.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    date.setMonth(date.getMonth() - 1);
    setSelectedMonth(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`);
  };

  const goToNextMonth = () => {
    const [year, month] = selectedMonth.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    date.setMonth(date.getMonth() + 1);
    setSelectedMonth(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`);
  };

  const goToCurrentMonth = () => {
    setSelectedMonth(new Date().toISOString().slice(0, 7));
  };

  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const months = ['Januar', 'Februar', 'Mart', 'April', 'Maj', 'Jun', 'Jul', 'Avgust', 'Septembar', 'Oktobar', 'Novembar', 'Decembar'];
    return `${months[parseInt(month) - 1]} ${year}`;
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-3xl shadow-2xl p-8 border border-gray-100">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">MedVista Backoffice</h1>
              <p className="text-gray-600">Prijavite se na svoj nalog</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Korisničko ime</label>
                <input
                  type="text"
                  value={loginForm.username}
                  onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                  required
                  autoComplete="username"
                  className="w-full px-4 py-3 text-sm border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Unesite korisničko ime"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Lozinka</label>
                <input
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  required
                  autoComplete="current-password"
                  className="w-full px-4 py-3 text-sm border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Unesite lozinku"
                />
              </div>

              {loginError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{loginError}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white py-4 rounded-xl font-bold hover:bg-blue-700 transition-all shadow-lg disabled:opacity-50"
              >
                {loading ? 'Prijava...' : 'Prijavite se'}
              </button>
            </form>
          </div>

          <div className="text-center mt-6">
            <a 
              href="https://medvista.online" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-semibold text-sm"
            >
              <ExternalLink className="w-4 h-4" />
              Idi na portal za zakazivanje
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Sidebar */}
      <div className={`fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden transition-opacity ${isSidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} onClick={() => setIsSidebarOpen(false)}></div>
      
      <div className={`fixed left-0 top-0 h-full bg-white shadow-xl z-50 transform transition-all duration-300 flex flex-col ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'} w-64`}>
        <div className="p-4 flex-1 overflow-y-auto">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              {tenantLogo ? (
                <img src={tenantLogo} alt="Logo" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
              ) : (
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-5 h-5 text-white" />
                </div>
              )}
              <div>
                <h2 className="text-base font-bold text-gray-900">MedVista</h2>
                <p className="text-xs text-gray-500">Backoffice</p>
              </div>
            </div>
            <button onClick={() => setIsSidebarOpen(false)} className="lg:hidden p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>

          <nav className="space-y-2">
            <button
              onClick={() => { 
                setCurrentPage('dashboard'); 
                markBookingsAsSeen();
                setIsSidebarOpen(window.innerWidth < 1024 ? false : isSidebarOpen); 
              }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors relative ${currentPage === 'dashboard' ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'}`}
              title="Termini"
            >
              <Home className="w-5 h-5 flex-shrink-0" />
              <span className="font-medium">Termini</span>
              {hasNewBookings && (
                <span className="absolute top-1 right-1 flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                </span>
              )}
            </button>
            
            <button
              onClick={() => { setCurrentPage('services'); setIsSidebarOpen(window.innerWidth < 1024 ? false : isSidebarOpen); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${currentPage === 'services' ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'}`}
              title="Usluge"
            >
              <Briefcase className="w-5 h-5 flex-shrink-0" />
              <span className="font-medium">Usluge</span>
            </button>

            <button
              onClick={() => { setCurrentPage('contacts'); setIsSidebarOpen(window.innerWidth < 1024 ? false : isSidebarOpen); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${currentPage === 'contacts' ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'}`}
              title="Imenik"
            >
              <BookUser className="w-5 h-5 flex-shrink-0" />
              <span className="font-medium">Imenik</span>
            </button>

            {isAdmin && (
              <button
                onClick={() => { setCurrentPage('tenant'); setIsSidebarOpen(window.innerWidth < 1024 ? false : isSidebarOpen); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${currentPage === 'tenant' ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'}`}
                title="Podaci"
              >
                <Building2 className="w-5 h-5 flex-shrink-0" />
                <span className="font-medium">Podaci</span>
              </button>
            )}

            {isAdmin && (
              <button
                onClick={() => { setCurrentPage('users'); setIsSidebarOpen(window.innerWidth < 1024 ? false : isSidebarOpen); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${currentPage === 'users' ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'}`}
                title="Administratori"
              >
                <Users className="w-5 h-5 flex-shrink-0" />
                <span className="font-medium">Administratori</span>
              </button>
            )}
            
            <button
              onClick={() => { setCurrentPage('profile'); setIsSidebarOpen(window.innerWidth < 1024 ? false : isSidebarOpen); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${currentPage === 'profile' ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'}`}
              title="Profil"
            >
              <Settings className="w-5 h-5 flex-shrink-0" />
              <span className="font-medium">Profil</span>
            </button>
          </nav>

          <div className="mt-8 pt-4 border-t border-gray-200">
            <button
              onClick={() => {
                setCurrentPage('profile');
                setIsSidebarOpen(window.innerWidth < 1024 ? false : isSidebarOpen);
              }}
              className="block w-full bg-blue-50 rounded-xl p-3 mb-3 hover:bg-blue-100 transition-colors text-left"
            >
              <div className="flex items-center gap-2 mb-1">
                <User className="w-4 h-4 text-blue-600" />
                <span className="text-xs font-semibold text-gray-700">Korisnik</span>
              </div>
              <p className="text-sm font-medium text-gray-900">{currentUser.full_name || currentUser.username}</p>
              <p className="text-xs text-gray-500 capitalize">{currentUser.role}</p>
            </button>

            <div className="bg-green-50 rounded-xl p-3 mb-3">
              <div className="flex items-center gap-2 mb-1">
                <Calendar className="w-4 h-4 text-green-600" />
                <span className="text-xs font-semibold text-gray-700">Organizacija</span>
              </div>
              <p className="text-sm font-medium text-gray-900">{currentUser.tenant_name}</p>
            </div>

            <a 
              href={`https://medvista.online/${currentUser.tenant_name}`}
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all text-sm font-semibold"
            >
              <ExternalLink className="w-4 h-4" />
              Portal za zakazivanje
            </a>
          </div>
        </div>

        <div className="p-4 border-t border-gray-200">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-xl transition-colors font-medium"
            title="Odjavi se"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            <span>Odjavi se</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col transition-all duration-300 ml-64">
        {/* Mobile Header */}
        <div className="lg:hidden bg-white shadow-sm p-4 sticky top-0 z-30 flex items-center justify-between">
          <button onClick={() => setIsSidebarOpen(true)} className="p-2 hover:bg-gray-100 rounded-lg">
            <Menu className="w-6 h-6" />
          </button>
          <h1 className="text-lg font-bold text-gray-900">MedVista</h1>
          <div className="relative">
            <button onClick={handleLogout} className="p-2 hover:bg-red-50 rounded-lg text-red-600">
              <LogOut className="w-5 h-5" />
            </button>
            {hasNewBookings && (
              <span className="absolute -top-1 -right-1 flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
              </span>
            )}
          </div>
        </div>

        {/* Dashboard Content */}
        {currentPage === 'dashboard' && (
          <div className="p-4 md:p-6 space-y-4 flex-1">
            {hasNewBookings && (
              <div className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-2xl shadow-lg p-4 flex items-center gap-3 animate-pulse">
                <Bell className="w-6 h-6" />
                <div className="flex-1">
                  <h3 className="font-bold">Novo zakazivanje!</h3>
                  <p className="text-sm">Imate nove termine za pregled</p>
                </div>
                <button 
                  onClick={markBookingsAsSeen}
                  className="px-4 py-2 bg-white text-blue-600 rounded-lg font-semibold hover:bg-blue-50"
                >
                  OK
                </button>
              </div>
            )}

            <div className="bg-white rounded-2xl shadow-lg p-4 md:p-6 border border-gray-100">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Zakazani termini</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <div className="md:col-span-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {viewMode === 'day' ? 'Datum' : 'Mesec'}
                  </label>
                  
                  {viewMode === 'day' ? (
                    <div className="flex items-center gap-2">
                      <button onClick={goToPreviousDay} className="p-2 hover:bg-gray-100 rounded-lg">
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <div className="flex-1 text-center">
                        <p className="text-base md:text-lg font-bold">{formatDate(selectedDate)}</p>
                      </div>
                      <button onClick={goToNextDay} className="p-2 hover:bg-gray-100 rounded-lg">
                        <ChevronRight className="w-5 h-5" />
                      </button>
                      <button onClick={goToToday} className="px-3 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 text-sm whitespace-nowrap">
                        Danas
                      </button>
                      <button onClick={() => setViewMode('month')} className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 text-sm whitespace-nowrap">
                        Mesec
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <button onClick={goToPreviousMonth} className="p-2 hover:bg-gray-100 rounded-lg">
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <div className="flex-1 text-center">
                        <p className="text-base md:text-lg font-bold">{formatMonth(selectedMonth)}</p>
                      </div>
                      <button onClick={goToNextMonth} className="p-2 hover:bg-gray-100 rounded-lg">
                        <ChevronRight className="w-5 h-5" />
                      </button>
                      <button onClick={goToCurrentMonth} className="px-3 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 text-sm whitespace-nowrap">
                        Ovaj mesec
                      </button>
                      <button onClick={() => setViewMode('day')} className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 text-sm whitespace-nowrap">
                        Dan
                      </button>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">Sve</option>
                    <option value="pending">Zakazano</option>
                    <option value="completed">Završeno</option>
                    <option value="verified">Potvrđeno</option>
                    <option value="unverified">Nepotvrđeno</option>
                    <option value="cancelled">Otkazano</option>
                  </select>
                </div>
              </div>

              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Pretraži po imenu, telefonu ili ID-u..."
                    className="w-full pl-10 pr-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="font-bold text-gray-900">Termini ({filteredBookings.length})</h3>
                <button 
                  onClick={() => loadBookings()} 
                  className="flex items-center gap-2 text-blue-600 hover:bg-blue-50 px-3 py-2 rounded-lg text-sm font-semibold transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  Osveži
                </button>
              </div>

              <div className="p-4">
                {filteredBookings.length === 0 ? (
                  <div className="text-center py-8">
                    <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">Nema termina</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredBookings.map((booking) => {
                      const status = getBookingStatus(booking);
                      const StatusIcon = status.icon;
                      const isVerified = booking.verified === 'true' || booking.verified === true;
                      const isCanceled = booking.canceled === 'true' || booking.canceled === true;
                      
                      return (
                        <div
                          key={booking.id}
                          className={`border-2 rounded-xl p-3 hover:shadow-lg transition-all cursor-pointer ${isCanceled ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
                          onClick={() => {
                            setSelectedBooking(booking);
                            setShowDetailsModal(true);
                          }}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                {isCanceled ? (
                                  <div className="px-3 py-1 rounded-full text-sm font-bold bg-red-500 text-white flex items-center gap-1">
                                    <XCircle className="w-4 h-4" />
                                    Otkazano
                                  </div>
                                ) : (
                                  <>
                                    <div className={`px-2 py-1 rounded-full text-xs font-bold bg-${status.color}-100 text-${status.color}-700 flex items-center gap-1`}>
                                      <StatusIcon className="w-3 h-3" />
                                      {status.label}
                                    </div>
                                    {isVerified && (
                                      <div className="px-2 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700 flex items-center gap-1">
                                        <CheckSquare className="w-3 h-3" />
                                        Potvrđeno
                                      </div>
                                    )}
                                    {!isVerified && (
                                      <div className="px-2 py-1 rounded-full text-xs font-bold bg-yellow-100 text-yellow-700 flex items-center gap-1">
                                        <AlertCircle className="w-3 h-3" />
                                        Nije potvrđeno
                                      </div>
                                    )}
                                  </>
                                )}
                                <span className="text-sm font-bold">{booking.schedule_start_time}</span>
                                {viewMode === 'month' && (
                                  <span className="text-xs text-gray-500">{booking.schedule_date}</span>
                                )}
                              </div>
                              
                              <h3 className={`text-base font-bold mb-1 truncate ${isCanceled ? 'text-red-700 line-through' : 'text-gray-900'}`}>
                                {booking.name} {booking.surname}
                              </h3>
                              
                              <div className="flex flex-wrap gap-2 text-xs text-gray-600">
                                <span className="flex items-center gap-1">
                                  <Phone className="w-3 h-3" />
                                  {booking.phone}
                                </span>
                              </div>
                              
                              <p className="text-xs text-gray-600 mt-1 truncate">{booking.service_type_rendered}</p>
                            </div>
                            
                            {!isCanceled && (
                              <div className="flex flex-col gap-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleVerified(booking);
                                  }}
                                  className={`p-2 rounded-lg font-bold shadow-sm ${isVerified ? 'bg-green-500 text-white hover:bg-green-600' : 'bg-yellow-400 text-yellow-900 hover:bg-yellow-500'}`}
                                  title={isVerified ? 'Potvrđeno' : 'Potvrdi'}
                                >
                                  {isVerified ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedBooking(booking);
                                    setShowDetailsModal(true);
                                  }}
                                  className="p-2 bg-blue-500 text-white hover:bg-blue-600 rounded-lg shadow-sm"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Services Content */}
        {currentPage === 'services' && (
          <div className="p-4 md:p-6 space-y-4 flex-1">
            <div className="bg-white rounded-2xl shadow-lg p-4 md:p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">Usluge</h2>
                {isAdmin && (
                  <button
                    onClick={() => {
                      setEditingService(null);
                      setServiceForm({
                        name: '',
                        description: '',
                        duration: '',
                        price: '',
                        picture: 'Stethoscope',
                        color: 'blue'
                      });
                      setShowServiceModal(true);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-semibold"
                  >
                    <Plus className="w-4 h-4" />
                    Dodaj
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {services.map((service) => {
                  const IconComponent = IconComponents[service.picture] || Stethoscope;
                  const colors = colorMap[service.color] || colorMap.blue;
                  
                  return (
                    <div key={service.id} className="border-2 border-gray-200 rounded-xl p-4 hover:shadow-lg transition-all">
                      <div className="flex items-start justify-between mb-3">
                        <div 
                          className="w-12 h-12 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: colors.bg, color: colors.text }}
                        >
                          <IconComponent className="w-6 h-6" />
                        </div>
                        {isAdmin && (
                          <div className="flex gap-1">
                            <button
                              onClick={() => {
                                setEditingService(service);
                                setServiceForm({
                                  name: service.name,
                                  description: service.description,
                                  duration: service.duration,
                                  price: service.price,
                                  picture: service.picture,
                                  color: service.color
                                });
                                setShowServiceModal(true);
                              }}
                              className="p-2 hover:bg-blue-50 rounded-lg text-blue-600"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => deleteService(service)}
                              className="p-2 hover:bg-red-50 rounded-lg text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                      <h3 className="font-bold text-gray-900 mb-1">{service.name}</h3>
                      <p className="text-sm text-gray-600 mb-2">{service.description}</p>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">{service.duration} min</span>
                        {service.price && <span className="font-bold text-blue-600">{service.price}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Contacts/Imenik Page */}
        {currentPage === 'contacts' && (
          <div className="p-4 md:p-6 space-y-4 flex-1">
            <div className="bg-white rounded-2xl shadow-lg p-4 md:p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">Imenik Zakazanih</h2>
                <button 
                  onClick={loadContacts} 
                  className="flex items-center gap-2 text-blue-600 hover:bg-blue-50 px-3 py-2 rounded-lg text-sm font-semibold"
                >
                  <RefreshCw className="w-4 h-4" />
                  Osveži
                </button>
              </div>

              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={contactsSearch}
                    onChange={(e) => setContactsSearch(e.target.value)}
                    placeholder="Pretraži kontakte..."
                    className="w-full pl-10 pr-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {filteredContacts.length === 0 ? (
                <div className="text-center py-8">
                  <BookUser className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">Nema kontakata</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredContacts.map((contact, index) => (
                    <ContactCard key={index} contact={contact} />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tenant Data Content */}
        {currentPage === 'tenant' && isAdmin && (
          <div className="p-4 md:p-6 space-y-4 flex-1">
            {tenantData ? (
              <div className="bg-white rounded-2xl shadow-lg p-4 md:p-6 border border-gray-100">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6">Podaci organizacije</h2>
                
                <div className="space-y-4 max-w-3xl">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Logo</label>
                    <div className="flex items-center gap-4">
                      {logoPreview && (
                        <div className="relative">
                          <img src={logoPreview} alt="Logo" className="w-20 h-20 rounded-lg object-cover border-2 border-gray-200" />
                          <button
                            onClick={removeLogo}
                            className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                            title="Ukloni logo"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                      <div className="flex-1">
                        <label className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer text-sm font-semibold text-gray-700">
                          <Upload className="w-4 h-4" />
                          {logoFile ? logoFile.name : 'Izaberi logo'}
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleLogoChange}
                            className="hidden"
                          />
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Naziv</label>
                      <input
                        type="text"
                        value={tenantForm.name}
                        onChange={(e) => setTenantForm({ ...tenantForm, name: e.target.value })}
                        className="w-full px-4 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Slogan</label>
                      <input
                        type="text"
                        value={tenantForm.description}
                        onChange={(e) => setTenantForm({ ...tenantForm, description: e.target.value })}
                        className="w-full px-4 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">O nama</label>
                    <textarea
                      value={tenantForm.about}
                      onChange={(e) => setTenantForm({ ...tenantForm, about: e.target.value })}
                      rows="4"
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 resize-none"
                    ></textarea>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Adresa</label>
                      <input
                        type="text"
                        value={tenantForm.address}
                        onChange={(e) => setTenantForm({ ...tenantForm, address: e.target.value })}
                        className="w-full px-4 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Mesto</label>
                      <input
                        type="text"
                        value={tenantForm.place}
                        onChange={(e) => setTenantForm({ ...tenantForm, place: e.target.value })}
                        className="w-full px-4 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Država</label>
                      <input
                        type="text"
                        value={tenantForm.country}
                        onChange={(e) => setTenantForm({ ...tenantForm, country: e.target.value })}
                        className="w-full px-4 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Telefon 1</label>
                      <input
                        type="text"
                        value={tenantForm.telephone}
                        onChange={(e) => setTenantForm({ ...tenantForm, telephone: e.target.value })}
                        className="w-full px-4 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Telefon 2</label>
                      <input
                        type="text"
                        value={tenantForm.telephone2}
                        onChange={(e) => setTenantForm({ ...tenantForm, telephone2: e.target.value })}
                        className="w-full px-4 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                      <input
                        type="email"
                        value={tenantForm.email}
                        onChange={(e) => setTenantForm({ ...tenantForm, email: e.target.value })}
                        className="w-full px-4 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Website</label>
                      <input
                        type="text"
                        value={tenantForm.web}
                        onChange={(e) => setTenantForm({ ...tenantForm, web: e.target.value })}
                        className="w-full px-4 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3">Radno vreme</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => {
                        const dayNames = {
                          monday: 'Ponedeljak',
                          tuesday: 'Utorak',
                          wednesday: 'Sreda',
                          thursday: 'Četvrtak',
                          friday: 'Petak',
                          saturday: 'Subota',
                          sunday: 'Nedelja'
                        };
                        return (
                          <div key={day}>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">{dayNames[day]}</label>
                            <input
                              type="text"
                              value={tenantForm[day]}
                              onChange={(e) => setTenantForm({ ...tenantForm, [day]: e.target.value })}
                              placeholder="08:00 - 16:00"
                              className="w-full px-4 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <button
                    onClick={handleTenantUpdate}
                    className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700 transition-all flex items-center justify-center gap-2"
                  >
                    <Save className="w-5 h-5" />
                    Sačuvaj izmene
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100 text-center">
                <p className="text-gray-500">Učitavanje...</p>
              </div>
            )}
          </div>
        )}

        {/* Users Management */}
        {currentPage === 'users' && isAdmin && (
          <div className="p-4 md:p-6 space-y-4 flex-1">
            <div className="bg-white rounded-2xl shadow-lg p-4 md:p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">Administratori</h2>
                <button
                  onClick={() => {
                    setEditingUser(null);
                    setUserForm({
                      username: '',
                      password: '',
                      full_name: '',
                      email: '',
                      role: 'staff',
                      is_active: 'true'
                    });
                    setShowUserModal(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-semibold"
                >
                  <Plus className="w-4 h-4" />
                  Dodaj
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {users.map((user) => (
                  <div key={user.id} className="border-2 border-gray-200 rounded-xl p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="font-bold text-gray-900">{user.full_name || user.username}</h3>
                        <p className="text-sm text-gray-600">@{user.username}</p>
                        <p className="text-xs text-gray-500 capitalize mt-1">{user.role}</p>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => {
                            setEditingUser(user);
                            setUserForm({
                              username: user.username,
                              password: '',
                              full_name: user.full_name,
                              email: user.email,
                              role: user.role,
                              is_active: user.is_active
                            });
                            setShowUserModal(true);
                          }}
                          className="p-2 hover:bg-blue-50 rounded-lg text-blue-600"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        {user.id !== currentUser.id && (
                          <button
                            onClick={() => deleteUser(user)}
                            className="p-2 hover:bg-red-50 rounded-lg text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="text-sm">
                      {user.email && <p className="text-gray-600">{user.email}</p>}
                      <div className={`inline-flex items-center mt-2 px-2 py-1 rounded-full text-xs font-bold ${(user.is_active === 'true' || user.is_active === true) ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {(user.is_active === 'true' || user.is_active === true) ? 'Aktivan' : 'Neaktivan'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Profile Content */}
        {currentPage === 'profile' && (
          <div className="p-4 md:p-6 space-y-4 max-w-2xl mx-auto w-full flex-1">
            <div className="bg-white rounded-2xl shadow-lg p-4 md:p-6 border border-gray-100">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6">Moj profil</h2>
              
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Rola</label>
                  <p className="text-base font-bold text-gray-900 capitalize">{currentUser.role}</p>
                  <p className="text-xs text-gray-500 mt-1">Ova vrednost se ne može menjati</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Puno ime</label>
                  <input
                    type="text"
                    value={profileForm.full_name}
                    onChange={(e) => setProfileForm({ ...profileForm, full_name: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    value={profileForm.email}
                    onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Promena lozinke</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Nova lozinka</label>
                      <input
                        type="password"
                        value={profileForm.new_password}
                        onChange={(e) => setProfileForm({ ...profileForm, new_password: e.target.value })}
                        autoComplete="new-password"
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Potvrdi lozinku</label>
                      <input
                        type="password"
                        value={profileForm.confirm_password}
                        onChange={(e) => setProfileForm({ ...profileForm, confirm_password: e.target.value })}
                        autoComplete="new-password"
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleProfileUpdate}
                  className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700 transition-all flex items-center justify-center gap-2"
                >
                  <Save className="w-5 h-5" />
                  Sačuvaj izmene
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <footer className="bg-white border-t border-gray-200 py-3 px-6 mt-auto">
          <div className="max-w-6xl mx-auto text-center">
            <p className="text-xs text-gray-500">
              Created and Hosted by{' '}
              <a 
                href="https://tecomatic.rs" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                Tecomatic
              </a>
              {' '}© 2025
            </p>
          </div>
        </footer>
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="text-xl font-bold">Detalji termina</h2>
              <button onClick={() => setShowDetailsModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {(selectedBooking.canceled !== 'true' && selectedBooking.canceled !== true) && (
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <span className="font-semibold text-gray-700">Status potvrde</span>
                  <button
                    onClick={() => toggleVerified(selectedBooking)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all ${(selectedBooking.verified === 'true' || selectedBooking.verified === true) ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                  >
                    {(selectedBooking.verified === 'true' || selectedBooking.verified === true) ? (
                      <>
                        <CheckSquare className="w-4 h-4" />
                        Potvrđeno
                      </>
                    ) : (
                      <>
                        <Square className="w-4 h-4" />
                        Potvrdi
                      </>
                    )}
                  </button>
                </div>
              )}

              {(selectedBooking.canceled === 'true' || selectedBooking.canceled === true) && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm font-semibold flex items-center gap-2">
                  <XCircle className="w-5 h-5" />
                  Ovaj termin je otkazan
                </div>
              )}

              <div>
                <h3 className="font-bold text-gray-900 mb-2">Pacijent</h3>
                <div className="bg-gray-50 rounded-xl p-3 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Ime:</span>
                    <span className="font-semibold">{selectedBooking.name} {selectedBooking.surname}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Godište:</span>
                    <span className="font-semibold">{selectedBooking.date_of_birth}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Telefon:</span>
                    <span className="font-semibold">{selectedBooking.phone}</span>
                  </div>
                  {selectedBooking.email && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Email:</span>
                      <span className="font-semibold">{selectedBooking.email}</span>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h3 className="font-bold text-gray-900 mb-2">Termin</h3>
                <div className="bg-gray-50 rounded-xl p-3 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">ID:</span>
                    <span className="font-semibold font-mono">{selectedBooking.book_id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Datum:</span>
                    <span className="font-semibold">{formatDate(selectedBooking.schedule_date)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Vreme:</span>
                    <span className="font-semibold">{selectedBooking.schedule_start_time} - {selectedBooking.schedule_end_time}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Usluga:</span>
                    <span className="font-semibold">{selectedBooking.service_type_rendered}</span>
                  </div>
                  {selectedBooking.note && (
                    <div className="pt-2 border-t border-gray-200">
                      <span className="text-gray-600 block mb-1">Napomena:</span>
                      <p className="text-gray-900">{selectedBooking.note}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-2">
                {(selectedBooking.synced !== 'true' && selectedBooking.synced !== true && selectedBooking.canceled !== 'true' && selectedBooking.canceled !== true) && (
                  <>
                    <button
                      onClick={() => {
                        setEditBookingForm({
                          schedule_date: selectedBooking.schedule_date,
                          schedule_start_time: selectedBooking.schedule_start_time,
                          schedule_end_time: selectedBooking.schedule_end_time
                        });
                        setShowEditModal(true);
                      }}
                      className="flex-1 bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 flex items-center justify-center gap-2 text-sm shadow-lg"
                    >
                      <Edit2 className="w-4 h-4" />
                      Izmeni
                    </button>
                    <button
                      onClick={() => markAsCompleted(selectedBooking)}
                      className="flex-1 bg-green-600 text-white py-3 rounded-xl font-semibold hover:bg-green-700 flex items-center justify-center gap-2 text-sm shadow-lg"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Završi
                    </button>
                    <button
                      onClick={() => cancelBooking(selectedBooking)}
                      className="flex-1 bg-red-600 text-white py-3 rounded-xl font-semibold hover:bg-red-700 flex items-center justify-center gap-2 text-sm shadow-lg"
                    >
                      <XCircle className="w-4 h-4" />
                      Otkaži
                    </button>
                  </>
                )}
                {(selectedBooking.synced === 'true' || selectedBooking.synced === true) && (
                  <div className="flex-1 bg-green-50 border-2 border-green-200 text-green-700 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4" />
                    Završeno
                  </div>
                )}
                {(selectedBooking.canceled === 'true' || selectedBooking.canceled === true) && (
                  <div className="flex-1 bg-red-50 border-2 border-red-200 text-red-700 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 text-sm">
                    <XCircle className="w-4 h-4" />
                    Otkazano
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Booking Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-bold">Izmeni termin</h2>
              <button onClick={() => setShowEditModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Datum</label>
                <input
                  type="date"
                  value={editBookingForm.schedule_date}
                  onChange={(e) => setEditBookingForm({ ...editBookingForm, schedule_date: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Početak</label>
                  <input
                    type="time"
                    value={editBookingForm.schedule_start_time}
                    onChange={(e) => setEditBookingForm({ ...editBookingForm, schedule_start_time: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Kraj</label>
                  <input
                    type="time"
                    value={editBookingForm.schedule_end_time}
                    onChange={(e) => setEditBookingForm({ ...editBookingForm, schedule_end_time: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <button
                onClick={handleEditBooking}
                className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700 flex items-center justify-center gap-2"
              >
                <Save className="w-5 h-5" />
                Sačuvaj
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Service Modal */}
      {showServiceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="text-xl font-bold">{editingService ? 'Izmeni uslugu' : 'Dodaj uslugu'}</h2>
              <button onClick={() => setShowServiceModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Naziv</label>
                <input
                  type="text"
                  value={serviceForm.name}
                  onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Opis</label>
                <textarea
                  value={serviceForm.description}
                  onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })}
                  rows="3"
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 resize-none"
                ></textarea>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Trajanje (min)</label>
                  <input
                    type="number"
                    value={serviceForm.duration}
                    onChange={(e) => setServiceForm({ ...serviceForm, duration: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Cena</label>
                  <input
                    type="text"
                    value={serviceForm.price}
                    onChange={(e) => setServiceForm({ ...serviceForm, price: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Ikonica</label>
                <select
                  value={serviceForm.picture}
                  onChange={(e) => setServiceForm({ ...serviceForm, picture: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                >
                  {availableIcons.map(icon => (
                    <option key={icon} value={icon}>{icon}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Boja</label>
                <div className="grid grid-cols-6 gap-2">
                  {availableColors.map(color => {
                    const colors = colorMap[color] || colorMap.blue;
                    return (
                      <button
                        key={color}
                        onClick={() => setServiceForm({ ...serviceForm, color: color })}
                        className={`w-full h-10 rounded-lg border-2 ${serviceForm.color === color ? 'border-gray-900 ring-2 ring-gray-400' : 'border-gray-300'} hover:scale-105 transition-transform`}
                        style={{ backgroundColor: colors.bg }}
                      ></button>
                    );
                  })}
                </div>
              </div>

              <button
                onClick={handleServiceSubmit}
                className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700 flex items-center justify-center gap-2"
              >
                <Save className="w-5 h-5" />
                Sačuvaj
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Modal */}
      {showUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="text-xl font-bold">{editingUser ? 'Izmeni korisnika' : 'Dodaj korisnika'}</h2>
              <button onClick={() => setShowUserModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Korisničko ime</label>
                <input
                  type="text"
                  value={userForm.username}
                  onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                  disabled={editingUser}
                  autoComplete="off"
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">{editingUser ? 'Nova lozinka (ostavite prazno da zadržite postojeću)' : 'Lozinka'}</label>
                <input
                  type="password"
                  value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                  autoComplete="new-password"
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Puno ime</label>
                <input
                  type="text"
                  value={userForm.full_name}
                  onChange={(e) => setUserForm({ ...userForm, full_name: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={userForm.email}
                  onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                  autoComplete="off"
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Rola</label>
                <select
                  value={userForm.role}
                  onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                >
                  <option value="staff">Osoblje</option>
                  <option value="administrator">Administrator</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                <select
                  value={userForm.is_active}
                  onChange={(e) => setUserForm({ ...userForm, is_active: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                >
                  <option value="true">Aktivan</option>
                  <option value="false">Neaktivan</option>
                </select>
              </div>

              <button
                onClick={handleUserSubmit}
                className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700 flex items-center justify-center gap-2"
              >
                <Save className="w-5 h-5" />
                Sačuvaj
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BackofficeApp;